import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Crown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TIERS = [
  { key: "free", name: "Free", price: { monthly: 0, yearly: 0 }, tagline: "Taste the model", features: ["5 top daily picks (probabilities hidden)", "Lab Favorites: lock, parlay & top SGPs of the day", "1 same game parlay per day", "Props board & live results", "Leaderboard, badges & referrals"] },
  { key: "basic", name: "Basic", price: { monthly: 19, yearly: 159 }, lookup: { monthly: "basic_monthly", yearly: "basic_yearly" }, tagline: "Full daily slate", features: ["All picks, all 18 leagues, with win probability & edge", "All same game parlays with alt lines", "Parlay builder: balanced builds up to 3 legs", "Save parlays up to 4 legs, alt-line ladder", "Bankroll stake sizing, results tracking, weekly recap"] },
  { key: "premium", name: "Premium", price: { monthly: 49, yearly: 399 }, lookup: { monthly: "premium_monthly", yearly: "premium_yearly" }, tagline: "The full laboratory", featured: true, features: ["Everything in Basic", "Builder on request: custom prompts, safe → longshot, 2–10 legs", "Player-prop pools in the AI builder", "Parlays up to 12 legs", "AI accuracy by confidence tier & full analytics"] },
];
const RANK = { free: 0, basic: 1, premium: 2 };

export function PricingTiers() {
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState("");
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const subscribe = async (tier) => {
    if (!user) return navigate("/login", { state: { from: "/pricing" } });
    if (tier.key === "free") return navigate(user.tier === "free" ? "/dashboard" : "/billing");
    setBusy(tier.key);
    try {
      if (user.stripe_subscription_id) {
        await api.post("/billing/switch", { lookup_key: tier.lookup[cycle] });
        await refreshUser();
        toast.success(`Switched to ${tier.name}`);
        return navigate("/billing");
      }
      const { data } = await api.post("/payments/checkout", { lookup_key: tier.lookup[cycle], origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div>
      <div className="mb-8 flex items-center gap-3">
        <span className={`text-sm ${cycle === "monthly" ? "text-white" : "text-slate-500"}`}>Monthly</span>
        <button data-testid="pricing-toggle-billing" onClick={() => setCycle((c) => (c === "monthly" ? "yearly" : "monthly"))} className="relative h-7 w-13 w-[52px] rounded-full border border-[#26334D] bg-[#121824] transition-colors">
          <span className={`absolute top-0.5 h-5.5 h-[22px] w-[22px] rounded-full bg-emerald-400 transition-transform duration-200 ${cycle === "yearly" ? "translate-x-[26px]" : "translate-x-0.5"}`} />
        </button>
        <span className={`text-sm ${cycle === "yearly" ? "text-white" : "text-slate-500"}`}>Yearly <span className="ml-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-bold text-emerald-400">Save 30%</span></span>
      </div>
      <div className="grid gap-5 md:grid-cols-3">
        {TIERS.map((t, i) => {
          const current = user?.tier === t.key && !(t.key !== "free" && user?.subscription_lookup_key && user.subscription_lookup_key !== t.lookup?.[cycle]);
          const downgrade = user && RANK[user.tier] > RANK[t.key] && !(user.stripe_subscription_id && t.key !== "free");
          return (
            <div key={t.key} data-testid={`tier-card-${t.key}`} className={`glass-card fade-up relative flex flex-col p-7 ${t.featured ? "glow-green !border-emerald-500/60" : ""}`} style={{ animationDelay: `${i * 90}ms` }}>
              {t.featured && <span className="absolute -top-3 left-6 flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#0B0E14]"><Crown size={12} /> Most popular</span>}
              <p className="label-caps">{t.tagline}</p>
              <h3 className="font-display mt-2 text-4xl font-black uppercase tracking-tight text-white">{t.name}</h3>
              <p className="mt-4 flex items-end gap-1"><span className="font-mono-num text-4xl font-bold text-white">${t.price[cycle]}</span><span className="mb-1.5 text-sm text-slate-500">/{cycle === "monthly" ? "mo" : "yr"}</span></p>
              <ul className="mt-6 flex-1 space-y-2.5">
                {t.features.map((f) => <li key={f} className="flex items-start gap-2 text-sm text-slate-300"><Check size={16} className="mt-0.5 shrink-0 text-emerald-400" /> {f}</li>)}
              </ul>
              <button data-testid={`btn-subscribe-${t.key}`} onClick={() => subscribe(t)} disabled={current || downgrade || busy === t.key} className={`mt-8 w-full ${t.featured ? "btn-primary" : "btn-ghost"}`}>
                {current ? "Current plan" : downgrade ? "Included" : busy === t.key ? "Redirecting…" : t.key === "free" ? (user && user.tier !== "free" ? "Manage in Billing" : "Start free") : user?.stripe_subscription_id ? `Switch to ${t.name}` : `Get ${t.name}`}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
